import React, { useState, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

import {
  validatePassword,
  validateEmail,
  validateOrganisationName,
  validateEmailDomain,
  validateEmailDomains,
  validateAdminEmailDomain,
  validateInvitationCode
} from "../utils/validation";
import { Alert } from "../components/Alert";
import { InvitationService } from "../services/invitationService";
import { OrganisationService } from "../services/organisationService";

interface Message {
  text: string;
  type: 'error' | 'success';
}

interface FieldErrors {
  [key: string]: boolean | string;
}

interface FormData {
  invitationCode: string;
  organisationName: string;
  adminFirstName: string;
  adminLastName: string;
  adminEmail: string;
  adminPassword: string;
  confirmPassword: string;
  emailDomains: string[];
}

export const OrganisationRegister: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const alertRef = useRef<HTMLDivElement>(null);

  const urlParams = new URLSearchParams(location.search);
  const inviteFromUrl = urlParams.get('invite') || '';
  const inviteFromState = (location.state as { invitationCode?: string })?.invitationCode || '';

  const [formData, setFormData] = useState<FormData>({
    invitationCode: inviteFromUrl || inviteFromState || '',
    organisationName: '',
    adminFirstName: '',
    adminLastName: '',
    adminEmail: '',
    adminPassword: '',
    confirmPassword: '',
    emailDomains: ['']
  });
  const [agreedToTos, setAgreedToTos] = useState(false);

  const [message, setMessage] = useState<Message>({ text: '', type: 'error' });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [invitationValidated, setInvitationValidated] = useState<boolean>(false);
  const [validatingInvitation, setValidatingInvitation] = useState<boolean>(false);

  const scrollToAlert = (): void => {
    if (alertRef.current) {
      alertRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
    }
  };

  const clearFieldError = (fieldName: string): void => {
    if (fieldErrors[fieldName]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    clearFieldError(name);
    
    // If invitation was validated but user changes email, reset validation
    if (name === 'adminEmail' && invitationValidated) {
      setInvitationValidated(false);
      setMessage({ text: '', type: 'error' });
    }
  };

  const handleDomainChange = (index: number, value: string): void => {
    const newDomains = [...formData.emailDomains];
    newDomains[index] = value;
    setFormData(prev => ({
      ...prev,
      emailDomains: newDomains
    }));
    clearFieldError(`emailDomain${index}`);
  };

  const addDomainField = (): void => {
    setFormData(prev => ({
      ...prev,
      emailDomains: [...prev.emailDomains, '']
    }));
  };

  const removeDomainField = (index: number): void => {
    if (formData.emailDomains.length > 1) {
      const newDomains = formData.emailDomains.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        emailDomains: newDomains
      }));
      // Clear any domain errors for removed field
      clearFieldError(`emailDomain${index}`);
    }
  };

  const handleTosChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setAgreedToTos(e.target.checked);
    clearFieldError('agreedToTos');
  };

  const validateInvitationCodeLocal = async (): Promise<boolean> => {
    const codeError = validateInvitationCode(formData.invitationCode);
    if (codeError) {
      setFieldErrors(prev => ({ ...prev, invitationCode: codeError }));
      setMessage({ text: codeError, type: 'error' });
      setTimeout(scrollToAlert, 100);
      return false;
    }

    setValidatingInvitation(true);
    try {
      const result = await InvitationService.validateInvitation(
        formData.invitationCode.trim().toUpperCase()
      );

      setInvitationValidated(true);
      setMessage({ text: result.message, type: 'success' });
      setTimeout(scrollToAlert, 100);

      clearFieldError('invitationCode');
      setValidatingInvitation(false);
      return true;
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to validate invitation code. Please try again.';
      setFieldErrors(prev => ({ ...prev, invitationCode: errorMessage }));
      setMessage({ text: errorMessage, type: 'error' });
      setTimeout(scrollToAlert, 100);
      setValidatingInvitation(false);
      return false;
    }
  };

  const validateForm = (): { errors: FieldErrors; errorMessage: string } => {
    const errors: FieldErrors = {};
    let firstErrorMessage: string = '';
    
  

    const organisationNameError = validateOrganisationName(formData.organisationName);
    if (organisationNameError) {
      errors.organisationName = true;
      if (!firstErrorMessage) firstErrorMessage = organisationNameError;
    }

    if (!formData.adminFirstName.trim()) {
      errors.adminFirstName = true;
      if (!firstErrorMessage) firstErrorMessage = 'Admin first name is required';
    }

    if (!formData.adminLastName.trim()) {
      errors.adminLastName = true;
      if (!firstErrorMessage) firstErrorMessage = 'Admin last name is required';
    }

    const emailError = validateEmail(formData.adminEmail);
    if (emailError) {
      errors.adminEmail = true;
      if (!firstErrorMessage) firstErrorMessage = emailError;
    }

    const passwordError = validatePassword(formData.adminPassword, formData.confirmPassword);
    if (passwordError) {
      errors.adminPassword = true;
      errors.confirmPassword = true;
      if (!firstErrorMessage) firstErrorMessage = passwordError;
    }

    const domainsError = validateEmailDomains(formData.emailDomains);
    if (domainsError) {
      errors.emailDomain0 = true;
      if (!firstErrorMessage) firstErrorMessage = domainsError;
    } else {
      formData.emailDomains.forEach((domain, index) => {
        if (domain.trim()) {
          const domainError = validateEmailDomain(domain);
          if (domainError) {
            errors[`emailDomain${index}`] = true;
            if (!firstErrorMessage) firstErrorMessage = domainError;
          }
        }
      });
    }

    if (!emailError && !domainsError) {
      const adminDomainError = validateAdminEmailDomain(formData.adminEmail, formData.emailDomains);
      if (adminDomainError) {
        errors.adminEmail = true;
        if (!firstErrorMessage) firstErrorMessage = adminDomainError;
      }
    }

    if (!agreedToTos) {
      errors.agreedToTos = true;
      if (!firstErrorMessage) firstErrorMessage = 'You must agree to the Terms of Service to register.';
    }

    return { errors, errorMessage: firstErrorMessage };
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ text: '', type: 'error' });
    setFieldErrors({});

    // Validate invitation code first if not already validated
    if (!invitationValidated) {
      const isInvitationValid = await validateInvitationCodeLocal();
      if (!isInvitationValid) {
        setIsLoading(false);
        return;
      }
    }

    const { errors, errorMessage } = validateForm();

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setMessage({ text: errorMessage, type: 'error' });
      setIsLoading(false);
      setTimeout(scrollToAlert, 100);
      return;
    }

    const payload = {
      invitation_code: formData.invitationCode.trim().toUpperCase(),
      organisation_name: formData.organisationName.trim(),
      country_code: 'AU',
      email_domains: formData.emailDomains
        .filter(domain => domain.trim())
        .map(domain => domain.trim().toLowerCase()),
      admin: {
        first_name: formData.adminFirstName.trim(),
        last_name: formData.adminLastName.trim(),
        email: formData.adminEmail.trim().toLowerCase(),
        password: formData.adminPassword,
        agreed_to_tos: agreedToTos
      }
    };

    try {
      const data = await OrganisationService.registerOrganisation(payload);

      setMessage({ text: data.message, type: 'success' });
      setTimeout(scrollToAlert, 100);
      setTimeout(() => {navigate('/login')}, 2000);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Registration failed. Please try again.';
      setMessage({ text: errorMessage, type: 'error' });
      setTimeout(scrollToAlert, 100);
    } finally {
      setIsLoading(false);
    }

  };

  const getInputClass = (fieldName: string): string => {
    return `w-full input ${fieldErrors[fieldName] ? 'input-error' : ''}`;
  };

  return (
    <div className="login-layout">
      <div className="w-full max-w-2xl p-6 space-y-4 bg-amber-50 rounded-lg shadow-md">
        <div className="text-center">
          <h1 className="text-3xl font-bold">Register Organisation</h1>
          <p className="text-base-content/70 mt-2">
            Create a new organisation account for LAB ZER0
          </p>
        </div>

        <div ref={alertRef}>
          {message.text && (
            <Alert type={message.type} message={message.text} />
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" noValidate>
          <div className="space-y-4">
            <h2 className="text-xl font-semibold border-b pb-2">Invitation</h2>
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Invitation Code</legend>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="invitationCode"
                  className={getInputClass('invitationCode')}
                  value={formData.invitationCode}
                  onChange={handleInputChange}
                  placeholder="Enter your invitation code"
                />
                {invitationValidated && (
                  <div className="btn btn-success btn-disabled">✓ Valid</div>
                )}
                {validatingInvitation && (
                  <div className="btn btn-disabled">
                    <span className="loading loading-spinner loading-sm"></span>
                  </div>
                )}
              </div>
              {/* <p className="label">You can only proceed if you were invited</p> */}
              <p className="label">
                {invitationValidated 
                  ? "Invitation code validated successfully"
                  : "Your invitation code will be validated when you submit the form"
                }
              </p>
            </fieldset>
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold border-b pb-2">Organisation Information</h2>
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Organisation Name</legend>
              <input
                type="text"
                name="organisationName"
                placeholder="e.g. My Organisation"
                className={getInputClass('organisationName')}
                value={formData.organisationName}
                onChange={handleInputChange}
              />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Country</legend>
              <input type="text" value="Australia" className="w-full input input-bordered" disabled />
            </fieldset>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Authorised Email Domains</legend>
              {formData.emailDomains.map((domain, index) => (
                <div key={index} className="flex gap-2 mb-2">
                  <input
                    type="text"
                    placeholder="researchcentre.org"
                    className={getInputClass(`emailDomain${index}`)}
                    value={domain}
                    onChange={(e) => handleDomainChange(index, e.target.value)}
                  />
                  {formData.emailDomains.length > 1 && (
                    <button
                      type="button"
                      className="btn btn-outline btn-error btn-square"
                      onClick={() => removeDomainField(index)}
                    >
                      ✕
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="btn btn-accent btn-sm mt-2"
                onClick={addDomainField}
              >
                + Add Domain
              </button>
              <p className="label">Users will be able to register with your organisation</p>
            </fieldset>
          </div>

          <div className="space-y-4">
            <h2 className="text-xl font-semibold border-b pb-2">Administrator Account</h2>

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Admin Email</legend>
              <input
                type="email"
                name="adminEmail"
                placeholder="you@researchcentre.org"
                className={getInputClass('adminEmail')}
                value={formData.adminEmail}
                onChange={handleInputChange}
              />
            </fieldset>

            <div>
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Admin First Name</legend>
                <input
                  type="text"
                  name="adminFirstName"
                  placeholder="Ada"
                  className={getInputClass('adminFirstName')}
                  value={formData.adminFirstName}
                  onChange={handleInputChange}
                  autoComplete="given-name"
                />
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Admin Last Name</legend>
                <input
                  type="text"
                  name="adminLastName"
                  placeholder="Lovelace"
                  className={getInputClass('adminLastName')}
                  value={formData.adminLastName}
                  onChange={handleInputChange}
                  autoComplete="family-name"
                />
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Admin Password</legend>
                <input
                  type="password"
                  name="adminPassword"
                  placeholder="••••••••"
                  className={getInputClass('adminPassword')}
                  value={formData.adminPassword}
                  onChange={handleInputChange}
                />
                <p className="label">Must be 8+ characters with uppercase, lowercase, and number</p>
              </fieldset>

              <fieldset className="fieldset">
                <legend className="fieldset-legend">Confirm Password</legend>
                <input
                  type="password"
                  name="confirmPassword"
                  placeholder="••••••••"
                  className={getInputClass('confirmPassword')}
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                />
              </fieldset>
            </div>

            <div className="space-y-2">
              <label className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  className={`checkbox ${fieldErrors.agreedToTos ? 'checkbox-error' : ''}`}
                  checked={agreedToTos}
                  onChange={handleTosChange}
                />
                <span className="text-sm">
                  I agree to the <Link to="/terms" className="text-primary hover:underline" target="_blank">Terms of Service</Link> and&nbsp;
                  <Link to="/privacy" className="text-primary hover:underline" target="_blank">Privacy Policy</Link>
                </span>
              </label>
            </div>
          </div>

          <button
            type="submit"
            className="w-full btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <span className="loading loading-spinner"></span>
                Registering Organisation...
              </>
            ) : (
              'Register Organisation'
            )}
          </button>
        </form>

        <div className="text-center space-y-2">
          <p className="text-sm">
          Already have an account? <Link to="/login" className="text-primary hover:underline">Login</Link>
          </p>
          <div className="divider text-xs">OR</div>
          <p className="text-sm">
          Need to register as a user? <Link to="/register" className="text-primary hover:underline">User Registration</Link>
          </p>
        </div>
      </div>
    </div>
  );
};
